import { useState } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  Download,
  Search,
} from "lucide-react";

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatQtd(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
}

function formatQtdCsv(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "";
  return Number(value).toFixed(4).replace(/\.?0+$/, "");
}

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function StockMovement() {
  const { user, isLoading: authLoading } = useGoogleAuth();

  // Se o usuário tem emitenteId vinculado, filtra por ele; caso contrário (admin/líder), mostra tudo
  const userEmitenteId = user?.emitenteId ?? null;
  const isCliente = !!userEmitenteId;

  const [filters, setFilters] = useState({
    itemCod: "",
    tipo: "todos" as "entrada" | "saida" | "todos",
    dataInicio: "",
    dataFim: "",
    filterEmitenteId: null as number | null,
  });
  const [appliedFilters, setAppliedFilters] = useState({ ...filters });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const { data: emitentesData } = trpc.emitentes.list.useQuery(undefined, {
    enabled: !authLoading,
  });
  const emitentes: any[] = emitentesData ?? [];

  const queryParams = {
    emitenteId: appliedFilters.filterEmitenteId ?? undefined,
    itemCod: appliedFilters.itemCod || undefined,
    tipo: appliedFilters.tipo,
    dataInicio: appliedFilters.dataInicio || undefined,
    dataFim: appliedFilters.dataFim || undefined,
    page,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading } = trpc.movimentacaoEstoque.listarComFiltros.useQuery(queryParams, {
    enabled: !authLoading,
    keepPreviousData: true,
  });
  const utils = trpc.useUtils();

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const totalEntradas = data?.totalEntradas ?? 0;
  const totalSaidas = data?.totalSaidas ?? 0;
  const custoEntradas = data?.custoEntradas ?? 0;
  const custoSaidas = data?.custoSaidas ?? 0;

  function handleApplyFilters() {
    setPage(1);
    setAppliedFilters({ ...filters });
  }

  function handleClearFilters() {
    const empty = { itemCod: "", tipo: "todos" as const, dataInicio: "", dataFim: "", filterEmitenteId: null as number | null };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  }

  async function handleExportRelatorioSaldoCSV() {
    if (!filters.dataFim) {
      toast.error("Preencha a data final para gerar o relatório de saldo");
      return;
    }

    try {
      const saldoRows = await utils.movimentacaoEstoque.relatorioSaldo.fetch({
        emitenteId: filters.filterEmitenteId ?? undefined,
        dataFim: filters.dataFim,
      });
      if (saldoRows.length === 0) {
        toast.error("Nenhum saldo encontrado para o emitente e a data final selecionados");
        return;
      }

      const headers = ["CÓDIGO", "DESCRIÇÃO", "EMITENTE", "QUANTIDADE", "CUSTO UNITÁRIO", "CUSTO TOTAL"];
      const csvRows = saldoRows.map((row: any) => [
        row.itemCod,
        row.descricao,
        row.emitenteNome,
        formatQtdCsv(row.quantidade).replace(".", ","),
        Number(row.custoUnitario).toFixed(4).replace(".", ","),
        Number(row.custoTotal).toFixed(4).replace(".", ","),
      ]);
      const csvContent = [headers, ...csvRows]
        .map((row) => row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
        .join("\n");
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `relatorio_saldo_${filters.dataFim}.csv`;
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Relatório de saldo exportado com sucesso");
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível gerar o relatório de saldo");
    }
  }

  function handleExportCSV() {
    if (!rows || rows.length === 0) {
      toast.error("Nenhum dado para exportar");
      return;
    }
    const headers = [
      "ID",
      "Data/Hora",
      "Emitente",
      "Cód. Produto",
      "Descrição",
      "Tipo",
      "Saldo Anterior",
      "Movimento",
      "Saldo Posterior",
      "Custo Unit. Anterior (R$)",
      "Custo Unit. Posterior (R$)",
      "Custo Movimento (R$)",
      "Pedido ID",
      "Orçamento ID",
    ];
    const csvRows = rows.map((r: any) => [
      r.id,
      formatDate(r.criado_em),
      r.emitente_nome,
      r.item_cod,
      r.item_descricao ?? "",
      Number(r.movimento) > 0 ? "Entrada" : "Saída",
      formatQtdCsv(r.saldo_anterior),
      formatQtdCsv(r.movimento),
      formatQtdCsv(r.saldo_posterior),
      Number(r.custo_anterior_unitario).toFixed(4),
      Number(r.custo_posterior_unitario).toFixed(4),
      Number(r.custo_movimento).toFixed(4),
      r.pedidoId > 0 ? r.pedidoId : "",
      r.orcamentoId > 0 ? r.orcamentoId : "",
    ]);

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
      .join("\n");

    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `movimentacao_estoque_${new Date().toISOString().split("T")[0]}.csv`;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("CSV exportado com sucesso");
  }

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Movimentação de Estoque</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportRelatorioSaldoCSV} disabled={!filters.dataFim}>
            <Download className="w-4 h-4 mr-2" />
            Relatório de Saldo
          </Button>
          <Button variant="outline" onClick={handleExportCSV} disabled={rows.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-1">
                <Label>Emitente</Label>
                <Select
                  value={filters.filterEmitenteId ? String(filters.filterEmitenteId) : "todos"}
                  onValueChange={(v) =>
                    setFilters((f) => ({ ...f, filterEmitenteId: v === "todos" ? null : Number(v) }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os emitentes</SelectItem>
                    {emitentes.map((em: any) => (
                      <SelectItem key={em.id} value={String(em.id)}>
                        {em.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            <div className="space-y-1">
              <Label>Produto (código ou descrição)</Label>
              <Input
                placeholder="Buscar produto..."
                value={filters.itemCod}
                onChange={(e) => setFilters((f) => ({ ...f, itemCod: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
              />
            </div>
            <div className="space-y-1">
              <Label>Tipo de Movimento</Label>
              <Select
                value={filters.tipo}
                onValueChange={(v) => setFilters((f) => ({ ...f, tipo: v as any }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="entrada">Entradas</SelectItem>
                  <SelectItem value="saida">Saídas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Data Início</Label>
              <Input
                type="date"
                value={filters.dataInicio}
                onChange={(e) => setFilters((f) => ({ ...f, dataInicio: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Data Fim</Label>
              <Input
                type="date"
                value={filters.dataFim}
                onChange={(e) => setFilters((f) => ({ ...f, dataFim: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleApplyFilters}>
              <Search className="w-4 h-4 mr-2" />
              Filtrar
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Entradas (qtd)</p>
                <p className="text-xl font-bold text-green-700">{formatQtd(totalEntradas)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Saídas (qtd)</p>
                <p className="text-xl font-bold text-red-700">{formatQtd(totalSaidas)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Custo Entradas</p>
                <p className="text-xl font-bold text-blue-700">{formatCurrency(custoEntradas)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <BarChart2 className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Custo Saídas</p>
                <p className="text-xl font-bold text-orange-700">{formatCurrency(custoSaidas)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Registros{" "}
              <span className="text-gray-400 font-normal text-sm">({total} no total)</span>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-40 text-gray-400">
              Carregando...
            </div>
          ) : rows.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-400">
              Nenhum registro encontrado para os filtros selecionados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                    <th className="px-4 py-3 text-left whitespace-nowrap">Data/Hora</th>
                    {!isCliente && <th className="px-4 py-3 text-left whitespace-nowrap">Emitente</th>}
                    <th className="px-4 py-3 text-left whitespace-nowrap">Produto</th>
                    <th className="px-4 py-3 text-left whitespace-nowrap">Tipo</th>
                    <th className="px-4 py-3 text-right whitespace-nowrap">Saldo Anterior</th>
                    <th className="px-4 py-3 text-right whitespace-nowrap">Movimento</th>
                    <th className="px-4 py-3 text-right whitespace-nowrap">Saldo Posterior</th>
                    <th className="px-4 py-3 text-right whitespace-nowrap">Custo Unit. Ant.</th>
                    <th className="px-4 py-3 text-right whitespace-nowrap">Custo Unit. Post.</th>
                    <th className="px-4 py-3 text-right whitespace-nowrap">Custo Mov.</th>
                    <th className="px-4 py-3 text-left whitespace-nowrap">Referência</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: any) => {
                    const isEntrada = Number(row.movimento) > 0;
                    return (
                      <tr key={row.id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {formatDate(row.criado_em)}
                        </td>
                        {!isCliente && (
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap text-xs">
                            {row.emitente_nome ?? "-"}
                          </td>
                        )}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{row.item_cod}</div>
                          {row.item_descricao && (
                            <div className="text-xs text-gray-500 max-w-[180px] truncate" title={row.item_descricao}>
                              {row.item_descricao}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {isEntrada ? (
                            <Badge className="bg-green-100 text-green-800 border-0">
                              <TrendingUp className="w-3 h-3 mr-1" />
                              Entrada
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 border-0">
                              <TrendingDown className="w-3 h-3 mr-1" />
                              Saída
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap font-mono text-xs">
                          {formatQtd(row.saldo_anterior)}
                        </td>
                        <td className={`px-4 py-3 text-right whitespace-nowrap font-mono text-xs font-semibold ${isEntrada ? "text-green-700" : "text-red-700"}`}>
                          {isEntrada ? "+" : ""}{formatQtd(row.movimento)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap font-mono text-xs">
                          {formatQtd(row.saldo_posterior)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap font-mono text-xs">
                          {formatCurrency(row.custo_anterior_unitario)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap font-mono text-xs">
                          {formatCurrency(row.custo_posterior_unitario)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap font-mono text-xs">
                          {formatCurrency(row.custo_movimento)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                          {row.pedidoId > 0 && (
                            <span className="inline-block bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded mr-1">
                              Ped #{row.pedidoId}
                            </span>
                          )}
                          {row.orcamentoId > 0 && (
                            <span className="inline-block bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">
                              Orc #{row.orcamentoId}
                            </span>
                          )}
                          {row.pedidoId === 0 && row.orcamentoId === 0 && "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-gray-500">
                Página {page} de {totalPages} &mdash; {total} registros
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
